"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, type ReactNode } from "react";

import { chromeLocaleForPath, type ChromeLocale } from "@/lib/chrome-locale";
import type { ChromeLink, HeaderChromeModel } from "@/lib/site-chrome";

/**
 * The sitewide header bar: logo, ONE navigation tree, and the actions.
 *
 * ## One tree for desktop and mobile
 *
 * The header used to print every link twice — a desktop bar plus a mobile
 * `<details>` menu with the same links (52 anchors for 29 targets on the
 * median page). Now there is one `<nav popover>`:
 *
 *   - below `xl` it is a popover the menu button opens (no JavaScript needed:
 *     `popovertarget` is declarative, and static artifacts strip scripts);
 *   - from `xl` up the closed popover's UA `display:none` is overridden, so the
 *     same list renders inline as the desktop bar.
 *
 * A browser without popover support ignores the attribute and shows the list as
 * a wrapped row under the logo, which is still usable.
 *
 * ## One language per page
 *
 * The root layout cannot read the pathname on the server without making every
 * page dynamic, so it passes BOTH language models and this component picks
 * one with `usePathname()` — during SSR as well, so the served HTML carries
 * only the page language's links (the way `AutoBreadcrumbs` works).
 *
 * The interactive pieces (search, theme, account, language switch) arrive as
 * slots from the server layout; they are not part of the chrome model.
 *
 * ## Phone bar: search, language, menu (xb-03, 2026-10-07)
 *
 * Below `xl` the language switch also sits in the bar as a 44x44 chip beside
 * search (`languageBar`); the menu keeps its copy. The bar stays one row at
 * 320px (logo 130 + three 44px controls + gaps), so the header height that
 * the sticky offsets are measured against does not change.
 *
 * Every chrome link carries `data-analytics-placement` (header_logo / _nav /
 * _tools / _cta / _shop); GlobalEngagementTracker records it on each click.
 */

export const SITE_NAV_ID = "site-nav";

export interface SiteHeaderSlots {
  /** The inline wordmark, rendered on the server (see SiteWordmark). */
  logo: ReactNode;
  palette: ReactNode;
  /** The language switch inside the menu (and the desktop bar from xl). */
  language: ReactNode;
  /** The compact language switch in the phone bar, beside search. */
  languageBar: ReactNode;
  theme: ReactNode;
  themeMenu: ReactNode;
  auth: ReactNode;
  authMenu: ReactNode;
}

export default function SiteHeaderBar({
  models,
  slots,
}: {
  models: Record<ChromeLocale, HeaderChromeModel>;
  slots: SiteHeaderSlots;
}) {
  const pathname = usePathname();
  const chrome = models[chromeLocaleForPath(pathname)];

  // A client-side navigation keeps the layout mounted, so an open menu would
  // stay open over the next page. Only a pathname CHANGE closes it: the menu
  // button is declarative (`popovertarget`) and works before hydration, so a
  // visitor who taps it while the page is still hydrating has a legitimately
  // open menu that the mount pass must not slam shut (caught by
  // tests/production/responsive-design.playwright.ts).
  const renderedPathname = useRef(pathname);
  useEffect(() => {
    if (renderedPathname.current === pathname) return;
    renderedPathname.current = pathname;
    const nav = document.getElementById(SITE_NAV_ID);
    if (nav && typeof nav.hidePopover === "function" && nav.matches(":popover-open")) {
      nav.hidePopover();
    }
  }, [pathname]);

  return (
    <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3 xl:flex-nowrap">
      <HeaderLogo chrome={chrome} logo={slots.logo} />
      <SiteNav chrome={chrome} slots={slots} />
      <div className="flex items-center gap-1.5 sm:gap-2 2xl:gap-2.5">
        {slots.palette}
        <div className="flex xl:hidden">{slots.languageBar}</div>
        <div className="hidden xl:block">{slots.theme}</div>
        <CtaButton link={chrome.ctas.astrologer} />
        <ShopLink link={chrome.ctas.shop} />
        {/* Fixed-dimension slot: HeaderAuth swaps a loading placeholder -> Sign in
            button -> account menu once the Supabase session resolves. Pinning the
            slot height (h-10) and width floor (min-w-[84px]) keeps that swap from
            changing the header height or the actions-group width. */}
        <div className="hidden h-10 min-w-[84px] items-center justify-end xl:flex">
          {slots.auth}
        </div>
        <button
          type="button"
          popoverTarget={SITE_NAV_ID}
          aria-label={chrome.menuLabel}
          className="flex size-11 cursor-pointer items-center justify-center rounded-md border border-border-default text-text-primary transition hover:bg-surface-brand-subtle xl:hidden"
        >
          <MenuIcon />
        </button>
      </div>
    </div>
  );
}

function HeaderLogo({ chrome, logo }: { chrome: HeaderChromeModel; logo: ReactNode }) {
  const { brand } = chrome;
  return (
    <Link
      href={brand.href}
      prefetch={false}
      className="flex shrink-0 items-center gap-2 text-brand-wordmark"
      aria-label={brand.homeLabel}
      data-analytics-placement="header_logo"
    >
      {/* Inline vector, not an image (b-hc-09): a lazy raster logo kept the
          brand off the first paint and showed a white box in dark mode, and an
          eager one put a preload in every document's head (rule 62, #1913).
          The slot is server-rendered, so its path data is not in this client
          module's bundle. SiteHeaderBar.test.tsx and site-chrome-parity pin it. */}
      {logo}
    </Link>
  );
}

function SiteNav({ chrome, slots }: { chrome: HeaderChromeModel; slots: SiteHeaderSlots }) {
  return (
    <nav
      id={SITE_NAV_ID}
      popover="auto"
      aria-label={chrome.navLabel}
      className={[
        // Mobile: the popover panel (only rendered while open; the UA hides it
        // otherwise). No display utility here — it would defeat that.
        "max-h-[calc(100dvh-8rem)] w-72 max-w-[calc(100vw-2rem)] basis-full overflow-y-auto rounded-xl border border-border-default bg-surface-raised py-2 text-text-primary shadow-xl",
        "open:fixed open:inset-auto open:right-4 open:top-20 open:m-0",
        // Desktop: the same list, inline in the header row.
        "xl:static xl:m-0 xl:flex xl:h-auto xl:w-auto xl:max-w-none xl:basis-auto xl:items-center xl:gap-3 xl:overflow-visible 2xl:gap-5 xl:border-0 xl:bg-transparent xl:p-0 xl:text-sm xl:text-text-secondary xl:shadow-none",
      ].join(" ")}
    >
      <ul className="xl:flex xl:items-center xl:gap-3 2xl:gap-5">
        {chrome.primaryNav.map((link) => (
          <li key={link.href}>
            <NavLink link={link} />
          </li>
        ))}
        <NavGroup group={chrome.navGroup} />
      </ul>
      <div className="px-4 py-2 xl:p-0" data-mobile-menu-language>
        {slots.language}
      </div>
      <div className="px-3 pb-2 xl:hidden" data-mobile-menu-theme>
        {slots.themeMenu}
      </div>
      <div className="px-3 pb-2 xl:hidden">{slots.authMenu}</div>
    </nav>
  );
}

function NavLink({ link }: { link: ChromeLink }) {
  return (
    <Link
      href={link.href}
      prefetch={false}
      data-analytics-placement="header_nav"
      className="flex min-h-11 items-center px-4 py-2 text-sm text-text-primary transition hover:bg-surface-brand-subtle xl:min-h-0 xl:whitespace-nowrap xl:p-0 xl:text-text-secondary xl:hover:bg-transparent xl:hover:text-text-primary"
    >
      {link.label}
    </Link>
  );
}

/**
 * The secondary links. In the mobile panel they are a labelled list; on the
 * desktop bar they open under their label on hover or keyboard focus — CSS
 * only, so the same markup works on statically served pages.
 */
function NavGroup({ group }: { group: HeaderChromeModel["navGroup"] }) {
  return (
    <li className="group relative mt-1 border-t border-border-default pt-1 xl:mt-0 xl:border-0 xl:pt-0">
      <span className="block px-4 pb-0.5 pt-1 text-xs font-bold uppercase tracking-widest text-text-muted xl:hidden">
        {group.label}
      </span>
      <button
        type="button"
        className="hidden cursor-pointer items-center gap-1 whitespace-nowrap text-text-secondary transition hover:text-text-primary xl:flex"
      >
        {group.label}
        <ChevronDownIcon />
      </button>
      <div className="xl:invisible xl:absolute xl:left-0 xl:top-full xl:z-50 xl:pt-2 xl:group-focus-within:visible xl:group-hover:visible">
        <ul className="xl:w-64 xl:rounded-xl xl:border xl:border-border-default xl:bg-surface-raised xl:py-2 xl:shadow-xl">
          {group.links.map((link) => (
            <li key={link.href}>
              <Link
                href={link.href}
                prefetch={false}
                data-analytics-placement="header_tools"
                className={`flex min-h-11 items-center px-4 py-2 text-sm transition hover:bg-surface-brand-subtle xl:min-h-0 ${
                  link.emphasis ? "font-semibold text-text-primary" : "text-text-secondary"
                }`}
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
}

function CtaButton({ link }: { link: ChromeLink }) {
  return (
    <Link
      href={link.href}
      prefetch={false}
      data-analytics-placement="header_cta"
      className="hidden min-h-11 items-center gap-1.5 whitespace-nowrap rounded-lg bg-surface-brand-strong px-3 py-2 text-sm font-semibold text-text-on-brand transition hover:opacity-90 md:flex md:px-3.5"
    >
      {link.label}
      <span className="hidden 2xl:inline" aria-hidden="true">
        <ArrowRightIcon />
      </span>
    </Link>
  );
}

function ShopLink({ link }: { link: ChromeLink }) {
  return (
    <Link
      href={link.href}
      prefetch={false}
      data-analytics-placement="header_shop"
      className="hidden min-h-11 items-center rounded-lg border border-border-default px-3 py-2 text-sm font-medium text-text-secondary transition hover:bg-surface-brand-subtle 2xl:flex"
    >
      {link.label}
    </Link>
  );
}

function ChevronDownIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      className="mt-0.5"
      aria-hidden="true"
    >
      <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M3 6h18M3 12h18M3 18h18" />
    </svg>
  );
}
